import {useEffect} from "react";

/**
 * Where a page's font comes from, as saved - never read from the font's link: the page's own fonts
 * (no link, no family), a Google Fonts stylesheet, or one uploaded font file.
 */
export type FontSource = 'STANDARD' | 'GOOGLE' | 'OWN';

/** A font as saved: its source, link and family change together. */
export interface FontValue {
    fontSource: FontSource
    /** Google Fonts: the stylesheet; own font: the uploaded file. */
    fontUrl?: string | null
    /** CSS font-family: a Google font's first family, an own font's named after the file. */
    fontFamily?: string | null
}

export const SYSTEM_SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

export const googleFontFamilies = (value: string): string[] => {
    try {
        const url = new URL(value);
        if (url.hostname !== 'fonts.googleapis.com') return [];

        return url.searchParams.getAll('family')
            .flatMap(family => family.split('|'))
            .map(family => family.split(':')[0].replace(/\+/g, ' ').trim())
            .filter(Boolean)
            .map(family => `"${family}", ${SYSTEM_SANS}`);
    } catch {
        return [];
    }
};

/** Google Fonts' stylesheet link for [family], in [weights] like '400;600', else in its default weight. */
export const googleFontsCss = (family: string, weights?: string): string => (
    `https://fonts.googleapis.com/css2?family=${family.trim().replace(/ /g, '+')}${weights ? `:wght@${weights}` : ''}&display=swap`
);

/** The family of a Google Fonts stylesheet link, e.g. "Lora" - the first of several. */
export const googleFontName = (fontUrl?: string | null): string | undefined => (
    fontUrl ? firstFontName(googleFontFamilies(fontUrl)[0]) : undefined
);

export interface GoogleFontLink {
    family: string
    /** A pasted stylesheet link, kept as it is; a font's page has none. */
    stylesheet?: string
}

/**
 * The Google font a pasted link names: a font's page on fonts.google.com, or a stylesheet link -
 * also inside Google's embed code. Undefined for anything else.
 */
export const googleFontFromLink = (text: string): GoogleFontLink | undefined => {
    const pasted = text.replace(/&amp;/g, '&');
    const stylesheet = pasted.match(/https:\/\/fonts\.googleapis\.com\/css2?\?[^\s"'<>)]+/)?.[0];
    if (stylesheet) {
        const family = googleFontName(stylesheet);
        return family ? {family, stylesheet} : undefined;
    }

    const specimen = pasted.match(/fonts\.google\.com\/specimen\/([^/?#\s"'<>]+)/)?.[1];
    const family = specimen && decodeURIComponent(specimen).replace(/\+/g, ' ').trim();
    return family ? {family} : undefined;
};

const FONT_FILE_NAME = /\.(woff2|woff|ttf|otf)$/i;

/** Whether a picked file is a font the page can use, by its name: WOFF2, WOFF, TTF or OTF. */
export const isFontFileName = (fileName: string): boolean => FONT_FILE_NAME.test(fileName.trim());

/** The first family of a CSS font family, unquoted: '"Effra", sans-serif' is "Effra". */
export const firstFontName = (fontFamily?: string | null): string | undefined => (
    fontFamily?.split(',')[0]?.trim().replace(/^["']|["']$/g, '').trim() || undefined
);

/** The family of an uploaded font file, named after the file: "Effra_WLatn_Lt.woff2" is "Effra WLatn Lt". */
export const ownFontFamily = (fileName: string): string => {
    const name = fileName.replace(/\.[^.]+$/, '').replace(/[^\p{L}\p{N}]+/gu, ' ').trim().slice(0, 60).trim();
    return `"${name || 'Own font'}", ${SYSTEM_SANS}`;
};

/**
 * Loads a font by its saved source, never by the look of its link: Standard loads nothing; a
 * Google font's stylesheet loads as a link; an own font's file through an @font-face of the font family's
 * first name, for every weight - so one file's titles aren't thickened into a fake bold, and a variable
 * font shows its real weights. Without a source - a signing snapshot issued before it was recorded - the
 * link loads as a stylesheet, as it always did.
 */
export const useFont = (
    fontSource: FontSource | undefined,
    fontUrl?: string | null,
    fontFamily?: string | null
) => {
    useEffect(() => {
        const url = fontUrl?.trim();
        if (fontSource === 'STANDARD' || !url || !/^https?:\/\//i.test(url)) return;

        let element: HTMLElement;
        if (fontSource === 'OWN') {
            const name = firstFontName(fontFamily);
            if (!name) return;
            element = document.createElement('style');
            element.textContent = `@font-face{font-family:${JSON.stringify(name)};src:url(${JSON.stringify(url)});font-weight:1 1000;font-display:swap}`;
        } else {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = url;
            element = link;
        }
        document.head.appendChild(element);

        return () => {
            element.remove();
        };
    }, [fontSource, fontUrl, fontFamily]);
};
